/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  type MouseEvent,
} from 'react';
import classNames from 'classnames';
import './NewsletterPanel.scss';
import useKeys from 'effects/keys';
import useGuid from 'effects/useGuid';
import CloseIcon from 'components/Icons/Exit/Exit';
import Newsletter, {
  type NewsletterHandle,
  type NewsletterProps,
} from 'components/Newsletter/Newsletter';
import type { FormField } from 'components/Form/Form';
import { createPortal } from 'react-dom';

const DEFAULT_REQUIRES_BIRTHDAY: ListValue = [];

const DEFAULT_TOGGLE: NonNullable<NewsletterPanelProps['toggle']> = {
  heading: 'Sign up for our',
  text: 'Newsletter',
};

const DEFAULT_RECEIPT = {
  heading: 'Thank you for your interest.',
  text: '<p>We have received your submission and will get back to you shortly.</p>',
};

const DEFAULT_LABELS = {
  close: 'Close',
};

const panelNode = document.getElementById('newsletter-panel-container');

function hasCommonID(array1: ListValue, array2: ListValue) {
  return array1.some(item => array2.includes(item));
}

type ListValue = (string | number)[];

export interface NewsletterPanelHandle {
  open: () => void;
  close: () => void;
}

interface NewsletterPanelProps {
  color?: string;
  requiresBirthday?: ListValue;
  toggle?: { classNames?: string; heading?: string; text?: string };
  newsletter: Omit<
    NewsletterProps,
    'form' | 'inactive' | 'onChange' | 'onSuccess'
  > & {
    heading?: string;
    text?: string;
    form: NonNullable<NewsletterProps['form']> & { fields: FormField[] };
  };
  receipt?: { heading?: string; text?: string };
  labels?: { close: string };
}

/**
 * This is the Newsletter Panel component.
 *
 * @author Thomas Sømoen <thomas.somoen@try.no>
 */
const NewsletterPanel = forwardRef<NewsletterPanelHandle, NewsletterPanelProps>(
  function NewsletterPanel(
    {
      color = 'black',
      requiresBirthday = DEFAULT_REQUIRES_BIRTHDAY,
      toggle = DEFAULT_TOGGLE,
      newsletter,
      receipt = DEFAULT_RECEIPT,
      labels = DEFAULT_LABELS,
    },
    ref
  ) {
    const container = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const formRef = useRef<NewsletterHandle>(null);
    const guid = useGuid();
    const [open, setOpen] = useState(false);
    const [birthday, setBirthday] = useState(false);
    const [completed, setCompleted] = useState(false);
    const newslists = newsletter.form.fields.find(field => field.id === 'list');

    useEffect(() => {
      if (
        newslists &&
        (newslists.value as ListValue).length &&
        requiresBirthday.length
      ) {
        if (hasCommonID(newslists.value as ListValue, requiresBirthday)) {
          setBirthday(true);
        }
      }
    }, [newslists, requiresBirthday]);

    useEffect(() => {
      if (open) {
        document.body.classList.add('newsletter-panel-open');
      }

      return function cleanup() {
        document.body.classList.remove('newsletter-panel-open');
      };
    }, [open]);

    useEffect(() => {
      if (open && formRef.current) {
        formRef.current.focus();
      }
    }, [open]);

    useImperativeHandle(ref, () => {
      return {
        open: () => setOpen(true),
        close: () => setOpen(false),
      };
    }, []);

    useKeys('featured', open, {
      esc: () => {
        setOpen(false);
      },
    });

    const onContainerClick = ({ target }: MouseEvent<HTMLDivElement>) => {
      if (target === container.current) {
        setOpen(false);
      }
    };

    function renderPanelContent() {
      if (completed) {
        return (
          <div className="receipt">
            <h2 className="h3 heading">{receipt.heading}</h2>
            <div
              className="body"
              dangerouslySetInnerHTML={{ __html: receipt.text ?? '' }}
            />
            <button
              className="ticket-button"
              onClick={() => setCompleted(false)}
            >
              <span>{labels.close}</span>
            </button>
          </div>
        );
      }

      const { heading, text = '', form, ...newsletterProps } = newsletter;

      const fields = form.fields.filter(field => {
        if (birthday) return true;
        return field.id !== 'birthday';
      });

      return (
        <>
          <h2 className="h3 heading">{heading}</h2>
          <div className="body" dangerouslySetInnerHTML={{ __html: text }} />
          <Newsletter
            ref={formRef}
            inactive={!open}
            {...newsletterProps}
            form={{
              ...form,
              fields,
            }}
            onChange={e => {
              if (e.id === 'list') {
                setBirthday(
                  hasCommonID(e.value as ListValue, requiresBirthday)
                );
              }
            }}
            onSuccess={() => {
              setCompleted(true);
            }}
          />
        </>
      );
    }

    function renderPanel() {
      const panel = (
        <div
          ref={container}
          id={`newsletter-panel-${guid}`}
          className={classNames('newsletter-panel', `theme-${color}`)}
          aria-hidden={!open}
          onClick={onContainerClick}
          role="dialog"
          aria-labelledby={`newsletter-panel-toggle-${guid}`}
          onTransitionEnd={e => {
            if (
              !open &&
              e.propertyName === 'transform' &&
              e.currentTarget === container.current &&
              buttonRef.current
            ) {
              buttonRef.current.focus();
            }
          }}
        >
          <div>
            <button
              className="exit"
              tabIndex={open ? undefined : -1}
              onClick={() => setOpen(false)}
              aria-label={labels.close}
            >
              <span>
                <CloseIcon title={labels.close} />
                <span className="text">{labels.close}</span>
              </span>
            </button>
            {renderPanelContent()}
          </div>
        </div>
      );

      if (panelNode) {
        return createPortal(panel, panelNode);
      }

      return panel;
    }

    return (
      <>
        <div
          className={classNames(
            'newsletter-panel-toggle-button',
            `theme-${color}`,
            toggle.classNames
          )}
        >
          <button
            className="toggle"
            ref={buttonRef}
            id={`newsletter-panel-toggle-${guid}`}
            data-testid={`newsletter-panel-toggle-${guid}`}
            aria-expanded={open}
            aria-controls={`newsletter-panel-${guid}`}
            onClick={() => setOpen(open => !open)}
          >
            <small>{toggle.heading}</small>
            <span>{toggle.text}</span>
          </button>
        </div>
        {renderPanel()}
      </>
    );
  }
);

export default NewsletterPanel;
